import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { isMfaVerified } from '@/lib/jwt';

/**
 * Auth status drives layout gating in routes/_authed.tsx.
 *
 * - anonymous: no credentials of any kind. Show /login.
 * - pre_mfa: holds a pre-MFA bearer issued by /v1/auth/login. Show /mfa.
 * - authed: holds a fully-MFA-verified access token. Admin routes pass.
 * - needs_mfa: a refresh succeeded but the new access token has MFAVerified=false.
 *              The user has an existing session refresh token but must re-attest.
 *              See plan.md §3.4 #2 — refresh alone is not enough for /admin/v1/*.
 */
export type AuthStatus = 'anonymous' | 'pre_mfa' | 'authed' | 'needs_mfa';

interface AuthState {
  status: AuthStatus;
  /** Full access token; MFAVerified=true when status='authed'. Memory-only. */
  accessToken: string | null;
  /** Pre-MFA bearer used by /v1/auth/mfa-challenge only. Memory-only. */
  preMfaToken: string | null;
  /** Refresh token. Persisted to sessionStorage (cleared on tab close). */
  refreshToken: string | null;
  /** User UUID from the auth response. Persisted alongside refreshToken. */
  userId: string | null;
  /** ISO timestamp when the access token expires. */
  accessExpiresAt: string | null;

  /**
   * Transient flag set by the enrollment flow so /mfa can show a "you just
   * enrolled — enter the next rotation" banner. Cleared by MfaForm on read.
   */
  justEnrolled: boolean;

  setLogin: (input: { accessToken: string; refreshToken?: string; userId: string; expiresAt: string }) => void;
  setPreMfa: (input: { accessToken: string; refreshToken?: string; userId: string; expiresAt: string }) => void;
  setNeedsMfa: () => void;
  setJustEnrolled: (v: boolean) => void;
  reset: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      status: 'anonymous',
      accessToken: null,
      preMfaToken: null,
      refreshToken: null,
      userId: null,
      accessExpiresAt: null,
      justEnrolled: false,

      setLogin: ({ accessToken, refreshToken, userId, expiresAt }) =>
        set({
          status: isMfaVerified(accessToken) ? 'authed' : 'pre_mfa',
          accessToken: isMfaVerified(accessToken) ? accessToken : null,
          preMfaToken: isMfaVerified(accessToken) ? null : accessToken,
          refreshToken: refreshToken ?? null,
          userId,
          accessExpiresAt: expiresAt,
        }),

      setPreMfa: ({ accessToken, refreshToken, userId, expiresAt }) =>
        set({
          status: 'pre_mfa',
          accessToken: null,
          preMfaToken: accessToken,
          refreshToken: refreshToken ?? null,
          userId,
          accessExpiresAt: expiresAt,
        }),

      setNeedsMfa: () =>
        set((s) => ({
          status: 'needs_mfa',
          accessToken: null,
          preMfaToken: s.accessToken ?? s.preMfaToken,
          accessExpiresAt: null,
        })),

      setJustEnrolled: (v) => set({ justEnrolled: v }),

      reset: () =>
        set({
          status: 'anonymous',
          accessToken: null,
          preMfaToken: null,
          refreshToken: null,
          userId: null,
          accessExpiresAt: null,
          justEnrolled: false,
        }),
    }),
    {
      name: 'plustown.auth',
      // sessionStorage — cleared on tab close. The refresh token lives here
      // because we need it to mint new access tokens after page reloads.
      //
      // We also persist the access token here so that direct URL navigation
      // (and hard reloads) within the same tab don't bounce a freshly-verified
      // operator back to /mfa (F-AD-13). The blast radius is unchanged — an
      // XSS attacker with sessionStorage access can already mint an access
      // token from the refresh token. Pre-MFA tokens stay memory-only since
      // their lifetime is bounded by the in-flight MFA challenge.
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({
        refreshToken: s.refreshToken,
        userId: s.userId,
        accessToken: s.accessToken,
        accessExpiresAt: s.accessExpiresAt,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // Trust the persisted access token only if it still looks MFA-verified
        // and unexpired. Otherwise drop it and fall through to the refresh
        // flow / login screen as before.
        const tokenLooksValid =
          typeof state.accessToken === 'string' &&
          isMfaVerified(state.accessToken) &&
          (!state.accessExpiresAt || new Date(state.accessExpiresAt) > new Date());
        if (tokenLooksValid) {
          state.status = 'authed';
          state.preMfaToken = null;
        } else if (state.refreshToken) {
          state.status = 'anonymous';
          state.accessToken = null;
          state.preMfaToken = null;
          state.accessExpiresAt = null;
        }
      },
    }
  )
);

/** Read access token without subscribing — use inside ky interceptor. */
export function readAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}
export function readPreMfaToken(): string | null {
  return useAuthStore.getState().preMfaToken;
}
export function readRefreshToken(): string | null {
  return useAuthStore.getState().refreshToken;
}
